'use client';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { BarChart3, Brain, FileBarChart2 } from 'lucide-react';
import { Plus, Minus } from 'lucide-react';

// useState
import { useState } from 'react';

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
  index: number;
}

const FAQItem = ({ question, answer, isOpen, onClick, index }: FAQItemProps) => {
  return (
    <motion.div
      className="border-b border-slate-700 last:border-b-0"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.1 * index }}>
      <button onClick={onClick} className="flex justify-between items-center w-full py-5 text-left focus:outline-none">
        <h3 className="text-lg font-medium text-white">{question}</h3>
        <div className="ml-2 flex-shrink-0">
          {isOpen ? <Minus className="h-5 w-5 text-blue-400" /> : <Plus className="h-5 w-5 text-blue-400" />}
        </div>
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden">
        <div className="pb-5 text-slate-300">{answer}</div>
      </motion.div>
    </motion.div>
  );
};

//   function FAQ() {

//     const fadeIn = {
//       hidden: { opacity: 0, y: 20 },
//       visible: { opacity: 1, y: 0 },
//     }

//     return (

//     )
//   }

export default function FeaturesPage() {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      question: 'How does the AI data analysis work?',
      answer:
        'Simply upload your data or use our sample datasets, then ask questions in natural language. The AI will interpret your queries and generate insights, visualizations, and predictions based on your specific data.',
    },
    {
      question: 'Do I need to know programming to use this tool?',
      answer:
        'Not at all! Our platform is designed to be user-friendly with a chat interface that allows you to interact with your data using natural language. No coding or technical expertise is required to get valuable insights from your data.',
    },
    {
      question: 'Is my data secure?',
      answer:
        'Yes, we take data security very seriously. All data is encrypted in transit and at rest. We store your data for 7 days, after which its automatically deleted. You can also manually delete your data at any time.',
    },
    {
      question: 'Can I export the analysis results?',
      answer:
        'You can export all visualizations, reports, and insights. This makes it easy to share your findings with colleagues or include them in presentations and reports.',
    },
    {
      question: 'I have a custom feature request. Can you help?',
      answer:
        'Yes, you can email us at support@slashml.com, or simply request a feature at https://plotsalot.featurebase.app/. We are always looking for ways to improve our platform and welcome your feedback.',
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-950 to-slate-900">
      <Navbar />

      <main className="flex-grow py-48">
        <div className="w-full max-w-3xl mx-auto">
          <motion.h1
            className="text-3xl sm:text-4xl font-bold text-white mb-8 text-center"
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>
            Frequently Asked Questions
          </motion.h1>

          <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700 rounded-lg overflow-hidden shadow-2xl p-4">
            <div className="divide-y divide-slate-700">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => toggleFAQ(index)}
                  index={index}
                />
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
